import { inkTo, waveable } from './ink-wave'
import { waveGeometry } from './ink-wave-geometry'
import { readsRtl, sigMs, sigNumber } from './motion'

const decoded = new WeakSet<ReturnType<typeof waveGeometry>>()

function decodeTiles(g: ReturnType<typeof waveGeometry>) {
  if (decoded.has(g) || typeof Image === 'undefined') return
  decoded.add(g)
  for (const tile of [g.wave, g.sheen]) {
    const image = new Image()
    image.src = tile.slice(5, -2)
    if (typeof image.decode === 'function') void image.decode().catch(() => {})
  }
}

/**
 * Pre-build and decode the tiles of every waved button on the page, without
 * adding any DOM layer. The size is `offsetWidth` and `offsetHeight`, the same
 * whole pixels the wave layer measures on hover, so the geometry cache hits.
 */
export function prewarmInkWaves(root: ParentNode = document): number {
  let warmed = 0
  for (const button of root.querySelectorAll<HTMLElement>('[data-slot="button"], .ink-wave-host')) {
    if (!waveable(button) || !button.offsetWidth || !button.offsetHeight) continue
    decodeTiles(
      waveGeometry(
        button.offsetWidth,
        button.offsetHeight,
        !readsRtl(button),
        sigNumber('--sig-wave-size', 6),
      ),
    )
    warmed++
  }
  return warmed
}

let prewarmQueued = false

/** One idle pre-warm after the buttons on a new screen render; repeated calls join it. */
function schedulePrewarm() {
  if (prewarmQueued) return
  prewarmQueued = true
  const run = () => {
    prewarmQueued = false
    // The timer can outlive its document: a test environment torn down within
    // the 200 ms wait threw `document is not defined` (ERP-51).
    if (typeof document !== 'undefined') prewarmInkWaves()
  }
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
  }
  if (idleWindow.requestIdleCallback) idleWindow.requestIdleCallback(run, { timeout: 500 })
  else window.setTimeout(run, 200)
}

/**
 * The ink wave's pointer listeners (DESIGN.md "The ink wave"): a mouse or pen
 * pointing at a button raises the wave and leaving drains it; a press of any
 * kind surges it to full, and a touch drains it once the surge has landed.
 * Keyboard focus keeps its ring and has no wave. Every listener is passive
 * and on the document: the wave never delays or blocks the click.
 *
 * A button is a `Button` (`data-slot="button"`) or any element styled with
 * `buttonVariants`, such as a link that looks like a button (`.ink-wave-host`).
 */

function buttonOf(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element
    ? target.closest<HTMLElement>('[data-slot="button"], .ink-wave-host')
    : null
}

function crossing(event: PointerEvent, button: HTMLElement | null): button is HTMLElement {
  return !!button && !(event.relatedTarget instanceof Node && button.contains(event.relatedTarget))
}

const onOver = (event: PointerEvent) => {
  const button = buttonOf(event.target)
  if (event.pointerType !== 'touch' && crossing(event, button))
    inkTo(button, true, '--sig-dur-wave')
}
const onOut = (event: PointerEvent) => {
  const button = buttonOf(event.target)
  if (event.pointerType !== 'touch' && crossing(event, button))
    inkTo(button, false, '--sig-dur-drain')
}
const onDown = (event: PointerEvent) => {
  const button = buttonOf(event.target)
  if (button && event.button === 0) inkTo(button, true, '--sig-dur-surge')
}
const onRelease = (event: PointerEvent) => {
  const button = buttonOf(event.target)
  if (!button || event.pointerType !== 'touch') return
  window.setTimeout(
    () => inkTo(button, false, '--sig-dur-drain'),
    sigMs('--sig-dur-surge', 140) + 80,
  )
}

const LISTENERS: [string, (event: PointerEvent) => void, AddEventListenerOptions][] = [
  ['pointerover', onOver, { passive: true }],
  ['pointerout', onOut, { passive: true }],
  ['pointerdown', onDown, { passive: true, capture: true }],
  ['pointerup', onRelease, { passive: true, capture: true }],
  ['pointercancel', onRelease, { passive: true, capture: true }],
]

let installed = false

/**
 * Install the document's five passive listeners, once, and queue a pre-warm.
 * `buttonVariants` calls this on every render, so each new screen's buttons
 * are warm before the pointer reaches them.
 */
export function installInkWave() {
  if (typeof document === 'undefined') return
  schedulePrewarm()
  if (installed) return
  installed = true
  for (const [type, listener, options] of LISTENERS) {
    document.addEventListener(type, listener as EventListener, options)
  }
}
